import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type ArCameraConsumer = "geo-ar" | "nature-vision";
type ArCameraStatus = "idle" | "requesting" | "live" | "error";

type ArCameraContextValue = {
  status: ArCameraStatus;
  error: string | null;
  stream: MediaStream | null;
  acquire(consumer: ArCameraConsumer): Promise<() => void>;
};

const ArCameraContext = createContext<ArCameraContextValue | null>(null);

export function SharedArCameraProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ArCameraStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pendingRef = useRef<Promise<MediaStream> | null>(null);
  const consumersRef = useRef(new Set<ArCameraConsumer>());

  const stopIfUnused = useCallback(() => {
    if (consumersRef.current.size > 0) return;
    const current = streamRef.current;
    if (current) current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    pendingRef.current = null;
    setStream(null);
    setStatus("idle");
  }, []);

  const ensureStream = useCallback(async () => {
    if (streamRef.current) return streamRef.current;
    if (pendingRef.current) return pendingRef.current;
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("CAMERA_API_UNAVAILABLE");
    }
    setStatus("requesting");
    setError(null);
    const pending = navigator.mediaDevices
      .getUserMedia({ audio: false, video: { facingMode: { ideal: "environment" } } })
      .then((nextStream) => {
        streamRef.current = nextStream;
        setStream(nextStream);
        setStatus("live");
        const track = nextStream.getVideoTracks()[0];
        track?.addEventListener("ended", () => {
          if (streamRef.current !== nextStream) return;
          streamRef.current = null;
          setStream(null);
          setStatus("idle");
        });
        return nextStream;
      })
      .catch((cause: unknown) => {
        const message = cause instanceof Error ? cause.message : "CAMERA_START_FAILED";
        setError(message);
        setStatus("error");
        throw cause;
      })
      .finally(() => {
        pendingRef.current = null;
      });
    pendingRef.current = pending;
    return pending;
  }, []);

  const acquire = useCallback(
    async (consumer: ArCameraConsumer) => {
      consumersRef.current.add(consumer);
      try {
        await ensureStream();
      } catch (cause) {
        consumersRef.current.delete(consumer);
        stopIfUnused();
        throw cause;
      }
      let released = false;
      return () => {
        if (released) return;
        released = true;
        consumersRef.current.delete(consumer);
        stopIfUnused();
      };
    },
    [ensureStream, stopIfUnused],
  );

  useEffect(() => () => {
    consumersRef.current.clear();
    const current = streamRef.current;
    if (current) current.getTracks().forEach((track) => track.stop());
  }, []);

  const value = useMemo<ArCameraContextValue>(
    () => ({ status, error, stream, acquire }),
    [status, error, stream, acquire],
  );

  return <ArCameraContext.Provider value={value}>{children}</ArCameraContext.Provider>;
}

export function useSharedArCamera() {
  const value = useContext(ArCameraContext);
  if (!value) throw new Error("SharedArCameraProvider is required.");
  return value;
}

export function SharedArCameraSurface() {
  const { stream, status } = useSharedArCamera();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    if (stream) void video.play().catch(() => undefined);
    return () => {
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [stream]);

  return (
    <div className="thera-ar-live-surface" data-camera-status={status} aria-busy={status === "requesting"}>
      {stream ? <video ref={videoRef} autoPlay muted playsInline aria-label="THERA AR live camera" /> : <div className="thera-ar-camera-transition" aria-hidden="true" />}
      {status === "error" ? <div className="thera-ar-camera-error" role="status">No se pudo iniciar la camara.</div> : null}
    </div>
  );
}
